import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Box, Button, Card, Paragraph, Text } from '@zillow/constellation-10';
import { ZRMUtils } from '@zg-rentals/rental-manager-header-navigation';
import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { logError } from '@zg-rentals/log-error';

import { PERMISSION_REQUEST_TYPE } from '../../constants/ApplicationPermissionsConstants';

import { VERTICAL_DISPLAY_TYPE } from '../../constants/PropertiesConstants';

import { RMWContext } from '../../providers/RMWContext';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';

import { RoutingSelectors, UserManagementSelectors } from '../../selectors';

import { useGetApplicationsSummary } from '../../hooks/queries/summary';
import { updateLoadingState } from '../../providers/LoadingMapContext/actions';

import ErrorRMW from '../../model/ErrorRMW';

import AlertWithTitle from '../AlertWithTitle';
import ApplicationsPermissionSetupModal from '../../components/Modal/ApplicationsPermissionSetupModal';
import IdVerificationOnboardingModal from '../Modal/IdVerificationOnboardingModal';
import EmptyApplicationsCardSimple from './components/EmptyApplicationsCardSimple';

import { useSelector } from '../../selectors/useSelector';

import UserManagementActions from '../../actions/UserManagementActions';

import ApplicationItem from './components/ApplicationItem';
import ApplicationsDisabledCard from './components/ApplicationsDisabledCard';
import ApplicationShareLinks from './components/ApplicationShareLinks';
import ApplicationsSummaryCard from './components/ApplicationsSummaryCard';
import ApplicationPermissionCard from './components/ApplicationPermissionCard';
import EmptyApplicationsCard from './components/EmptyApplicationsCard';
import ApplicationsHeader from './components/ApplicationsHeader';

import ConnectedModuleCard from '../ConnectedModuleCard';

import type { ReactNode } from 'react';
import type { Application } from './components/ApplicationItem';
import type { TRIGGER_LOCATIONS } from '../../constants/ClickstreamConstants';

type Props = {
  displayType?: VERTICAL_DISPLAY_TYPE;
  updateMinModuleHeight?: () => void;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
};

const MOCK_APPLICATION = {
  statusType: '',
  applicants: [
    {
      name: '',
      status: '',
    },
  ],
  applicationIsViewed: false,
  statusUpdatedTimestamp: Date.now(),
  conversationId: '',
  applicationId: '',
};

const ConnectedApplications = ({
  triggerLocation,
  displayType = VERTICAL_DISPLAY_TYPE.FULL,
  updateMinModuleHeight,
}: Props) => {
  const dispatch = useDispatch();
  const { paths } = useContext(RMWContext);

  const {
    reducerDispatch,
    loadingByComponent: { ConnectedApplications: isConnectedApplicationsLoading },
  } = useContext(LoadingMapContext);

  const {
    isMultifamily,
    user,
    userManagement: { applicationPermissions },
  } = useSelector(
    (state) => ({
      isMultifamily: state.properties.currentProperty.isMultifamily,
      user: state.user,
      userManagement: state.userManagement,
    }),
    shallowEqual,
  );

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));
  const location = useSelector((state) => RoutingSelectors.makeLocation(state));
  const search = useSelector((state) => RoutingSelectors.makeSearch(state));
  const applicationManager = useSelector((state) => UserManagementSelectors.makeApplicationManager(state));
  const { verifyIdentitySuccess = 'false' } = search ? ZRMUtils.queryUtils.parse(search) : {};

  const [permissionRequestType, setPermissionRequestType] = useState<keyof typeof PERMISSION_REQUEST_TYPE | null>(null);
  const [showAppsPermissionModal, setShowAppsPermissionModal] = useState(verifyIdentitySuccess === 'true');

  // use isLoading condition in order apply conditional logic on the initial request
  const {
    data: applicationsSummary,
    isPending,
    error: applicationsSummaryError = {},
  } = useGetApplicationsSummary(listingId, true);

  const {
    urlPath = '',
    rentalApplicationData: { applicationList = [], totalNumberOfApplications },
    isApplicationsAccepted,
    userManagementFlags: { canEnableApplications },
    inviteOnly: { isInviteOnly },
  } = applicationsSummary || { rentalApplicationData: {}, userManagementFlags: {}, inviteOnly: {} };

  // @ts-expect-error
  const error = applicationsSummaryError?.error;

  const hasNameAndIsIdVerified = user.firstName && user.lastName && user.isIdVerified;

  const applicationCardListLength = applicationsSummary?.rentalApplicationData?.applicationList?.length || 0;
  const hasRecentApplications = applicationCardListLength > 0 || isConnectedApplicationsLoading;

  const { hasClientError, clientError } = useMemo(() => {
    let hasClientError = false;
    let errorRMWModel = {};

    const { applicationsOwnerEmail, isApplicationsAccessRestricted } = applicationsSummary || {};

    if (isApplicationsAccessRestricted) {
      hasClientError = true;

      errorRMWModel = ErrorRMW({
        code: 'isApplicationsAccessRestricted',
        message:
          user?.email && applicationsOwnerEmail
            ? `You are signed in as ${user.email}. This information is only available to ${applicationsOwnerEmail}. To see this information, you need to switch accounts.`
            : 'This information is not available for your account. To see this information, you need to switch accounts.',
        data: applicationsSummary,
      });
    }

    return {
      hasClientError,
      clientError: errorRMWModel,
    };
  }, [applicationsSummary]);

  useEffect(() => {
    if (applicationsSummaryError) {
      logError({
        error: applicationsSummaryError,
        context: 'ConnectedApplications@useGetApplicationsSummary',
      });
    }
  }, [applicationsSummaryError]);

  useEffect(() => {
    if (user.userManagementCompanyId) {
      dispatch(UserManagementActions.getUsersWithApplicationPermissionDetails(user.userManagementCompanyId));
    }
  }, [user.userManagementCompanyId]);

  useEffect(() => {
    if (updateMinModuleHeight) {
      updateMinModuleHeight();
    }
  }, []);

  useEffect(() => {
    if (applicationsSummary && applicationsSummary?.userManagementFlags?.isEligible) {
      const {
        userManagementFlags: { canRequestAccess, canEnableApplications, isApplicationPermissionsEnabledForCompany },
      } = applicationsSummary;

      // if a user is a company admin or has been granted permission, isIdVerified, and has entered a first and last name. The checks for name and isIdverified is so that people spoof enabling applications by adding a queryParam of verifyIdentitySuccess to enable applications w/o being verified
      if (isApplicationPermissionsEnabledForCompany && canEnableApplications && hasNameAndIsIdVerified) {
        return setPermissionRequestType(null);
      }

      if (!canEnableApplications && !canRequestAccess) {
        setPermissionRequestType(PERMISSION_REQUEST_TYPE.NO_PERMISSION);
      }

      if (isApplicationPermissionsEnabledForCompany) {
        if (canRequestAccess) {
          // company admin has already enabled applications for listing, user just needs to request permission to be able to view them
          setPermissionRequestType(PERMISSION_REQUEST_TYPE.REQUEST_ACCESS);
        }
      } else if (canEnableApplications) {
        // user is a company admin and has the power to enable applications for the listing. The company admin will become an application manager and be able to grand permission to other users to see apps
        setPermissionRequestType(PERMISSION_REQUEST_TYPE.SET_APPLICATION_MANAGER);
      }
    }
  }, [applicationsSummary, applicationPermissions.isLoading]);

  useEffect(() => {
    updateLoadingState({
      reducerDispatch,
      data: {
        applicationsSummary: isPending,
      },
    });
  }, [isPending]);

  const handleSetApplicationManagerClick = () => {
    if (
      applicationsSummary?.userManagementFlags?.isEligible &&
      !applicationManager &&
      user.isPropertyManagerCompanyAdmin
    ) {
      setShowAppsPermissionModal(true);
    }
  };

  if (error || hasClientError) {
    const errorObj = hasClientError ? clientError : error;

    const errorContent: { title?: null | string; body: ReactNode } = {
      title: null,
      body: <Text>There was an error fetching your applications. Please try again!</Text>,
    };

    if (errorObj?.code === 'isApplicationsAccessRestricted') {
      const { pathname, search } = location;
      const to = paths.signInPage(`${pathname}${search}`);
      errorContent.title = 'Access unavailable';
      errorContent.body = (
        <>
          <Paragraph css={{ marginTop: '200' }} className="fs-mask">
            {errorObj?.message}
          </Paragraph>
          <Box css={{ marginTop: '400' }}>
            <Button asChild tone="brand" emphasis="filled" size="sm" fluid>
              <a href={to || undefined}>Switch Accounts</a>
            </Button>
          </Box>
        </>
      );
    }

    if (errorObj?.data?.partialMessages) {
      errorContent.body = <PartialMessage partialMessages={error?.data?.partialMessages} />;
    }

    return (
      <Box data-testid="ConnectedApplications-ApplicationsError" css={{ marginTop: { base: '200', md: '0' } }}>
        {displayType === VERTICAL_DISPLAY_TYPE.FULL ? (
          <ConnectedModuleCard
            title="Applications"
            // @ts-expect-error
            padding={{ default: 'sm', xl: 'lg' }}
          >
            <AlertWithTitle appearance="warning" body={errorContent.body} title={errorContent.title} />
          </ConnectedModuleCard>
        ) : (
          <Card elevated={false} css={{ padding: { base: '400', xl: '800' } }}>
            <AlertWithTitle appearance="warning" body={errorContent.body} title={errorContent.title} />
          </Card>
        )}
      </Box>
    );
  }

  const renderFullType = ({
    applicationsToRender,
    showSummaryCard,
  }: {
    applicationsToRender: Array<Application>;
    showSummaryCard: boolean;
  }) => {
    return (
      <>
        {/* includes a view more cta only to be displayed when application card list is not empty  */}
        <ApplicationsHeader
          // @ts-expect-error
          applicationsSummary={applicationsSummary}
          permissionType={permissionRequestType}
          onSetApplicationManagerClick={handleSetApplicationManagerClick}
        />

        <Box
          css={{
            paddingBottom: { base: hasRecentApplications ? '400' : '0', xl: hasRecentApplications ? '600' : '0' },
          }}
        />

        <Box
          css={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: { base: '200', xl: '600' } }}
        >
          {applicationsToRender.map((application: Application, idx: number) => {
            return (
              <Box css={{ gridColumn: { base: '1/3', lg: 'auto' } }} key={`ApplicationItem-${idx}`}>
                <ApplicationItem application={application} urlPath={urlPath} displayType={displayType} />
              </Box>
            );
          })}

          {showSummaryCard && (
            <Box css={{ gridColumn: { base: '1/3', lg: 'auto' } }}>
              {/* @ts-expect-error */}
              <ApplicationsSummaryCard applicationsSummary={applicationsSummary} />
            </Box>
          )}
        </Box>

        <Box
          css={{
            paddingBottom: { base: hasRecentApplications ? '400' : '0', xl: hasRecentApplications ? '600' : '0' },
          }}
        />

        <ApplicationShareLinks
          hasRecentApplications={hasRecentApplications}
          isTertiaryButtonStyling={false}
          permissionType={permissionRequestType}
          applicationsSummary={applicationsSummary}
          onSetApplicationManagerClick={handleSetApplicationManagerClick}
        />
      </>
    );
  };

  const renderSimpleType = ({
    applicationsToRender,
    showSummaryCard,
  }: {
    applicationsToRender: Array<Application>;
    showSummaryCard: boolean;
  }) => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(1, 1fr)',
            lg: applicationCardListLength < 2 ? 'repeat(1, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
          },
          gap: { base: '200', lg: '400' },
        }}
      >
        {applicationsToRender.map((application: Application, idx: number) => {
          return (
            <Box key={`application-${idx}`}>
              <ApplicationItem application={application} urlPath={urlPath} displayType={displayType} />
            </Box>
          );
        })}

        {showSummaryCard && (
          <Box>
            <ApplicationsSummaryCard
              // @ts-expect-error
              applicationsSummary={applicationsSummary}
              displayType={VERTICAL_DISPLAY_TYPE.SIMPLE}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderContent = () => {
    if (isConnectedApplicationsLoading) {
      return displayType === VERTICAL_DISPLAY_TYPE.FULL
        ? renderFullType({
            applicationsToRender: Array(2).fill({ ...MOCK_APPLICATION }),
            showSummaryCard: false,
          })
        : renderSimpleType({
            applicationsToRender: Array(2).fill({ ...MOCK_APPLICATION }),
            showSummaryCard: false,
          });
    }

    if (permissionRequestType) {
      return (
        <ApplicationPermissionCard
          // @ts-expect-error
          applicationsSummary={applicationsSummary}
          permissionType={permissionRequestType}
          onSetApplicationManagerClick={handleSetApplicationManagerClick}
        />
      );
    }

    if (!isApplicationsAccepted && !isInviteOnly) {
      return (
        <ApplicationsDisabledCard
          canEnableApplications={isMultifamily ? Boolean(canEnableApplications) : true}
          displayType={displayType}
          setShowAppsPermissionModal={setShowAppsPermissionModal}
          triggerLocation={triggerLocation}
        />
      );
    }

    if (isApplicationsAccepted || isInviteOnly) {
      const showSummaryCard = applicationsSummary && Number(totalNumberOfApplications) > 4;
      const applicationsToRender = showSummaryCard ? applicationList.slice(0, 3) : applicationList;

      // TODO: can refactor EmptyApplicationsCard to handle EmptyApplicationsCardSimple display type
      if (totalNumberOfApplications === 0 || applicationCardListLength === 0) {
        if (displayType === VERTICAL_DISPLAY_TYPE.FULL) {
          return <EmptyApplicationsCard applicationsSummary={applicationsSummary} />;
        }

        if (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
          return <EmptyApplicationsCardSimple displayType={displayType} />;
        }
      }

      if (displayType === VERTICAL_DISPLAY_TYPE.FULL) {
        return renderFullType({ applicationsToRender, showSummaryCard: Boolean(showSummaryCard) });
      }

      if (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
        return renderSimpleType({ applicationsToRender, showSummaryCard: Boolean(showSummaryCard) });
      }
    }

    return null;
  };

  return (
    <Box data-testid="ConnectedApplications" css={{ marginTop: { base: '200', md: '0' } }}>
      <Card
        elevated={false}
        outlined={hasRecentApplications ? true : false}
        css={{ padding: hasRecentApplications ? { base: '400', xl: '800' } : '0' }}
      >
        {renderContent() || <></>}
      </Card>

      {showAppsPermissionModal && (
        <ApplicationsPermissionSetupModal
          setShowApplicationsPermissionSetupModal={setShowAppsPermissionModal}
          verifyIdentitySuccess={Boolean(verifyIdentitySuccess === 'true' && hasNameAndIsIdVerified)}
        />
      )}
      <IdVerificationOnboardingModal applicationsUrlPath={urlPath} />
    </Box>
  );
};

export { ConnectedApplications };
export default ConnectedApplications;
